import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CONSTANT,
  FileUploadService,
  LoaderService,
  ObservableService,
  OrderService,
  PdfGeneratorService,
} from '@bst-frontend/shared';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs';

export type VehicleAssignMode = 'create' | 'edit' | 'view';

export interface VehicleAssignData {
  id?: string;
  truck: string;
  mobileNo: string;
  lrNumber: string;
  lrPdfUrl: string;
  tempoNo: string;
  notes: string;
}

@Component({
  selector: 'bst-frontend-assign-truck-modal',
  templateUrl: './assign-truck-modal.component.html',
  styleUrls: ['./assign-truck-modal.component.css'],
})
export class AssignTruckModalComponent {
  @Input() mode: VehicleAssignMode = 'create';
  @Input() initialData?: any;
  @Input() subOrder?: any;

  @ViewChild('lrTemplate') lrTemplate!: ElementRef<HTMLDivElement>;

  form!: FormGroup;
  loading = false;
  generating = false;

  get isViewMode() {
    return this.mode === 'view';
  }

  @Input() orderData: any;
  trucksList: any = [];
  orderTruckDetailForm!: FormGroup;
  selectedTruck: any;
  uploadedFileKey: string | null = null;
  uploadedFileSize: number | null = null;
  s3Url = '';
  // lrNumber = '';
  today = new Date();
  totlalQty = 0;
  totalWeight = 0
  constructor(
    private activeModal: NgbActiveModal,
    private orderService: OrderService,
    private fb: FormBuilder,
    private loader: LoaderService,
    private observableService: ObservableService,
    private pdfService: PdfGeneratorService,
    private uploadService: FileUploadService,
    private cdRef: ChangeDetectorRef,
    @Inject('environment') private environment: any
  ) {}

  async ngOnInit() {
    this.loader.show();
    this.s3Url = `${this.environment.s3UrlDownload}`;
    this.buildForm();
    await this.getTruckList();
    this.calculateTotal();

    if (this.initialData) {
      this.selectedTruck = await this.getSelectedTruck(
        this.initialData.truckId
      );
      this.form.patchValue({
        truckNo: this.initialData.truckNo,
        mobileNo: this.initialData.mobileNo,
        tempoNo: this.initialData.tempoNo,
        notes: this.initialData.notes,
        lrPdfUrl: this.initialData?.lrAttachment?.path
          ? this.s3Url + this.initialData.lrAttachment.path
          : '',
        lrNumber: this.initialData?.lrAttachment?.name
          ? this.initialData.lrAttachment.name.replace(/\.pdf$/i, '')
          : '',
      });
      // this.lrNumber = this.initialData?.lrAttachment?.name || '';
    }

    if (this.isViewMode) {
      this.form.disable();
    }
  }

  calculateTotal() {
    this.totlalQty = 0;
    this.totalWeight = 0;
    if (this.orderData?.type === 'full-load' && this.subOrder?.deliveries) {
      this.subOrder.deliveries.forEach((delivery: any) => {
        if (delivery.items) {
          delivery.items.forEach((it: any) => {
            if (it?.planned?.qty) {
              this.totlalQty += it.planned.qty;
              this.totalWeight += it.planned.weight;
            }
          });
        }
      });
    } else if ((this.orderData?.type === 'part-load-door' || this.orderData?.type === 'part-load-office') && this.subOrder?.pickup?.items) {
      this.subOrder.pickup.items.forEach((it: any) => {
        if (it?.planned?.qty) {
          this.totlalQty += it.planned.qty;
          this.totalWeight += it.planned.weight;
        }
      });
    }
  }

  private buildForm() {
    this.form = this.fb.group({
      truckNo: ['', Validators.required],
      mobileNo: [''],
      tempoNo: ['', this.orderData.type !== 'full-load' &&  Validators.required],
      notes: [''],
      lrPdfUrl: ['', Validators.required], // will be filled after upload
      lrNumber: [''],
    });
  }


  getTotal(charges: any[]) {
    let total = 0;
    if (charges && charges.length > 0) {
      charges.forEach((item) => {
        total += item.charge;
      });
    }
    return total;
  }

  async getTruckList() {
    try {
      const res: any = await this.orderService.getAllTruck().toPromise();
      this.trucksList = res.data;
    } catch (error) {
      console.error('Failed to load trucks', error);
    }
  }

  // convenience getter
  get f() {
    return this.form.controls;
  }

  markAllTouched() {
    this.form.markAllAsTouched();
  }

  // === LR GENERATE / UPDATE ===

  async onGenerateOrUpdatePdf() {
    this.generating = true;
    try {
      this.loader.show();
      // 1) ensure LR number exists (can auto-generate here if you want)
      // if (!this.lrNumber) {
      //   const res: any = await this.orderService.genrateLRNumber().toPromise();
      //   this.lrNumber = res.data;
      // }

      // 2) build filename
      // const fileName = `${this.lrNumber}.pdf`;
      const fileName = `${this.f['lrNumber'].value}.pdf`;

      this.cdRef.detectChanges();
      // 3) get pdf blob from template
      const pdfBlob = await this.pdfService.generatePdfFromElement(
        this.lrTemplate.nativeElement
      );

      // 4) upload to S3
      this.uploadService
        .uploadFileToS3(pdfBlob, fileName, 'lr', this.orderData._id)
        .pipe(
          finalize(() => {
            this.generating = false;
            this.loader.hide();
          })
        )
        .subscribe({
          next: ({ fileUrl, fileKey }) => {
            this.uploadedFileKey = fileKey;
            this.uploadedFileSize = pdfBlob.size;
            this.form.patchValue({ lrPdfUrl: fileUrl });
          },
          error: (err) => {
            console.error('Upload error', err);
            alert('Failed to generate/upload LR PDF');
          },
        });
    } catch (e) {
      this.generating = false;
      alert('Failed to generate LR PDF');
      this.loader.hide();
    }
  }

  // === DOWNLOAD ===
  async onDownloadPdf() {
    const url = this.f['lrPdfUrl'].value;
    if (!url) {
      return;
    }

    // easiest: open new tab OR force download via hidden <a>
    // const a = document.createElement('a');
    // a.href = url;
    // a.download = this.lrNumber || 'lr.pdf';
    // a.target = '_blank';
    // document.body.appendChild(a);
    // a.click();
    // document.body.removeChild(a);

    try {
      const res = await fetch(url, { method: 'GET' });

      if (!res.ok) throw new Error('Download failed');

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${this.f['lrNumber'].value}.pdf` || 'lr.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('PDF download failed', err);
    }
  }

  async getSelectedTruck(truckId: string) {
    return await this.trucksList.find(
      (e: { _id: string }) => e._id === truckId
    );
  }

  async changeTruck(e: any) {
    if (e) {
      this.selectedTruck = await this.getSelectedTruck(e);
    } else {
      this.selectedTruck = undefined;
    }
  }

  async save() {
    if (this.form.invalid) {
      this.markAllTouched();
      return;
    }

    // Required fields
    const truckId = this.selectedTruck?._id; // from dropdown list
    const truckNo = this.f['truckNo'].value;
    const mobileNo = this.f['mobileNo'].value;
    // const lrNumber = this.lrNumber;
    const tempoNo = this.f['tempoNo'].value;
    const notes = this.f['notes'].value;

    // LR data from upload
    const fileKey = this.uploadedFileKey;
    const fileName = (fileKey && fileKey.split('/').pop()) ?? 'lr.pdf';
    const lrSize = this.uploadedFileSize;
    let lrAttachment;
    if (fileKey) {
      lrAttachment = {
        file: '',
        name: fileName,
        path: fileKey,
        size: lrSize,
      };
    } else {
      lrAttachment = this.initialData.lrAttachment;
    }
    // Assemble final payload
    const payload = {
      truckId: truckId,
      totalLimit: this.selectedTruck?.totalLimit ?? 0,
      availableLimit: this.selectedTruck?.availableLimit ?? 0,
      truckNo: this.selectedTruck?.truckNumber,
      mobileNo: mobileNo,
      tempoNo: tempoNo,
      lrNumber: this.f['lrNumber'].value,
      lrAttachment,
      notes: notes,
    };

    this.loader.show();
    try {
      await this.orderService
        .updateSubOrder(
          CONSTANT.orderStatus['ASSING-TRUCK'],
          this.subOrder._id,
          payload
        )
        .toPromise();
      this.observableService.send({ refresh: true });
      // this.loader.hide();
    } catch (error) {
      console.log('Error', error);
    }

    this.activeModal.close({ status: 'saved' });
  }

  getSerialNumber(di: number, itt: number): number {
  let sr = 0;

  // count items from previous deliveries
  for (let d = 0; d < di; d++) {
    sr += this.subOrder?.deliveries?.[d]?.items?.length || 0;
  }

  // add current item index
  return sr + itt + 1;
}


  cancel() {
    this.activeModal.dismiss('cancel');
  }
}
